<template>
  <Layout>
    <div :class="$style.appsumoOauthRedirect">
      <div class="w-full md:w-80 m-auto">
        <template v-if="registrationLoading">
          <template v-if="licenseLoading">
            <p>Processing your AppSumo integration...</p>
          </template>

          <template v-else>
            <Loader/>
          </template>
        </template>

        <div v-else :class="$style.registrationForm">
          <el-form
              ref="registrationForm"
              :model="registrationForm"
              :rules="formRules"
              label-width="100px"
              label-position="top"
              status-icon
              :disabled="!hashedLicenseKey"
              @submit.native.prevent="formSubmit">
            <header>
              <h3 class="text-body-black !mb-0">Welcome Sumo-ling!</h3>

              <span class="text-body-black text-xs">Your account is almost ready</span>
            </header>

            <div v-show="registrationError" class="e-server-error !mb-4">
              {{ registrationErrorMessage }}
            </div>

            <el-form-item class="basic" label="Username" prop="username">
              <el-input v-model="registrationForm.username" placeholder=""></el-input>
            </el-form-item>

            <el-form-item class="basic" label="Email" prop="email">
              <el-input v-model="registrationForm.email" placeholder="" type="email"></el-input>
            </el-form-item>

            <el-form-item class="basic" label="Password" prop="password1">
              <el-input
                  v-model="registrationForm.password1"
                  type="password"
                  placeholder=""
                  @change="setSecondPassword"
                  @keyup.enter.native="formSubmit"/>
            </el-form-item>

            <el-form-item v-if="recaptchaActive">
              <VueRecaptcha
                  ref="recaptcha"
                  size="invisible"
                  :sitekey="siteKey"
                  @verify="onCaptchaVerified"
                  @expired="onCaptchaExpired"/>
            </el-form-item>

            <el-form-item>
              <button ref="registrationBtn"
                      class="btn primary"
                      type="button"
                      :disabled="registrationLoading || !hashedLicenseKey"
                      @click="formSubmit">Register
              </button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from 'axios'
import { APIEndpoint } from '@api/_version'
import VueRecaptcha from 'vue-recaptcha'
import { signupComputed, signupMethods } from '@state/helpers'
import Layout from '@layouts/main.vue'

export default {
  name: 'AppSumoOAuthRedirect',
  page: {
    title: 'AppSumo License Activation',
    meta: [{ name: 'description', content: 'Register AppSumo License' }],
  },
  components: { Layout, VueRecaptcha },
  data() {
    return {
      licenseLoading: true,
      error: null,
      // needsRegistration: true,
      registrationForm: {
        username: '',
        email: '',
        password1: '',
        password2: '',
      },
      formRules: {
        username: [
          { required: true, message: 'Username is required.', trigger: 'blur' },
          { min: 3, message: 'Username must be at least 3 characters.', trigger: 'blur' },
        ],
        email: [
          { required: true, message: 'Email is required.', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email.', trigger: 'blur' },
        ],
        password1: [
          { required: true, message: 'Password is required.', trigger: 'blur' },
          { min: 6, message: 'Password must be at least 6 characters.', trigger: 'blur' },
        ],
      },
      recaptchaActive: process.env.NODE_ENV === 'production',
      siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
      hashedLicenseKey: '',
    }
  },
  computed: {
    ...signupComputed,
  },
  beforeCreate() {
    if (typeof window.Onvocado.recaptchaLoaded === 'undefined') {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
      window.Onvocado.recaptchaLoaded = true
    }
  },
  created() {
    const code = this.$route.query.code

    if (!code) {
      this.error = 'Authorization code not found in URL.'
      this.$message({
        type: 'error',
        duration: 0,
        offset: 60,
        message: this.error,
      })
      this.licenseLoading = false
      return
    }

    // Send the authorization code to the backend
    axios
        .get(`${APIEndpoint}/api/appsumo/callback/`, {
          params: { code }, // Ensures `code` is sent as a query parameter
        })
        .then((response) => {
          // Store the hashed_license_key if present
          if (response.data.license_key) {
            this.hashedLicenseKey = response.data.license_key

            this.$message({
              type: 'success',
              message: 'AppSumo Licence detected! 🌮 & 🥑',
              offset: 60,
            })
          }
        })
        .catch((error) => {
          this.error = 'Failed to complete AppSumo OAuth. Please try again.'
          this.$message({
            type: 'error',
            duration: 0,
            message: this.error,
            offset: 60,
          })
          console.error(error)
        })
        .finally(() => {
          this.licenseLoading = false
        })
  },
  methods: {
    ...signupMethods,
    formSubmit() {
      this.$refs.registrationForm.validate((valid) => {
        if (valid) {
          if (this.recaptchaActive) {
            this.$refs.recaptcha.execute()
          } else {
            this.submitRegistration()
          }
        }
      })
    },
    async submitRegistration(recaptchaToken = 'dev') {

      // Prepare the registration data, including hashed_license_key
      const data = {
        ...this.registrationForm,
        recaptchaToken,
        licenseKey: this.hashedLicenseKey,
      }

      this.createAccount(data).then(() => {
        if (this.registrationCompleted) {
          this.$router.push({ name: 'settings:billing' })
        }
      })
    },
    setSecondPassword() {
      this.registrationForm.password2 = this.registrationForm.password1
    },
    onCaptchaVerified(recaptchaToken) {
      this.$refs.recaptcha.reset()
      this.submitRegistration(recaptchaToken)
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset()
      this.$message({
        type: 'warning',
        message: 'reCAPTCHA expired. Please try again.',
      })
    },
  },
}
</script>

<style lang="scss" module>
@import '@assets/sass/design/_mixin.scss';

.appsumoOauthRedirect {
  @include pageDefaults;
  @apply mx-4 md:mx-auto;

  height: calc(100vh - 130px);
  display: flex;
  align-items: center;
}

.registrationForm {
  max-width: 400px;
  margin: 0 auto;
  text-align: left;
}
</style>